import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { CountryPageData, HomePageData } from '../../../shared/models/marketing.models';
import { MarketingConsultationCtaComponent } from '../../components/marketing-consultation-cta.component';
import { MarketingCountryCostsComponent } from '../../components/marketing-country-costs.component';
import { MarketingFaqComponent } from '../../components/marketing-faq.component';
import { MarketingFooterComponent } from '../../components/marketing-footer.component';
import { MarketingGlobalSuccessStoriesComponent } from '../../components/marketing-global-success-stories.component';
import { MarketingHeaderComponent } from '../../components/marketing-header.component';
import { MarketingHeroComponent } from '../../components/marketing-hero.component';
import { MarketingScholarshipsComponent } from '../../components/marketing-scholarships.component';
import { MarketingSuccessRoadmapComponent } from '../../components/marketing-success-roadmap.component';
import { MarketingSectionHeadingComponent } from '../../components/marketing-section-heading.component';
import { MarketingStatsStripComponent } from '../../components/marketing-stats-strip.component';
import { MarketingTestimonialsComponent } from '../../components/marketing-testimonials.component';
import { PagesService } from '../../../shared/services/pages.service';
import { BlogsService } from '../../../shared/services/blogs.service';
import type { BlogListItem } from '../../../shared/models/blog.models';
import { MarketingBlogCountryStripComponent } from '../../components/marketing-blog-country-strip.component';
import { buildPageTitle } from '../../../shared/constants/site-meta';
import { resolveMilestoneCtaHref } from '../../utils/milestone-cta';

export type CountryPageResolved = { error: true; page?: undefined } | { error: false; page: CountryPageData };

@Component({
  selector: 'app-country-page',
  standalone: true,
  imports: [
    AsyncPipe,
    RouterLink,
    MarketingHeaderComponent,
    MarketingHeroComponent,
    MarketingStatsStripComponent,
    MarketingSectionHeadingComponent,
    MarketingCountryCostsComponent,
    MarketingScholarshipsComponent,
    MarketingSuccessRoadmapComponent,
    MarketingGlobalSuccessStoriesComponent,
    MarketingTestimonialsComponent,
    MarketingFaqComponent,
    MarketingConsultationCtaComponent,
    MarketingBlogCountryStripComponent,
    MarketingFooterComponent,
  ],
  templateUrl: './country-page.component.html',
})
export class CountryPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly pages = inject(PagesService);
  private readonly blogs = inject(BlogsService);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);

  readonly country$: Observable<CountryPageResolved> = this.route.data.pipe(
    map((d) => d['countryPage'] as CountryPageResolved),
  );

  /** Global success stories + testimonials (same content as home page). */
  readonly homeContent = toSignal(
    this.pages.getMarketingHome().pipe(catchError(() => of(null))),
    { initialValue: null as HomePageData | null },
  );

  readonly countryBlogs = toSignal(
    this.route.paramMap.pipe(
      switchMap((pm) => {
        const slug = pm.get('slug');
        if (!slug) return of([] as BlogListItem[]);
        return this.blogs.getPublicCountryListing(slug, 1, 3).pipe(
          map((page) => page.items),
          catchError(() => of([] as BlogListItem[])),
        );
      }),
    ),
    { initialValue: [] as BlogListItem[] },
  );

  constructor() {
    const snap = this.route.snapshot.data['countryPage'] as CountryPageResolved | undefined;
    if (snap && !snap.error && snap.page) this.applySeo(snap.page);
  }

  testimonialItems(home: HomePageData) {
    return home.testimonials?.items ?? [];
  }

  successStoryItems(home: HomePageData) {
    return (home.successStories?.items ?? []).map((story) => ({
      ...story,
      ctaHref: resolveMilestoneCtaHref(story.ctaHref, story.title),
    }));
  }

  hasGlobalStoriesSection(home: HomePageData | null): home is HomePageData {
    if (!home) return false;
    return this.successStoryItems(home).length > 0 || this.testimonialItems(home).length > 0;
  }

  private applySeo(page: CountryPageData): void {
    const seo = page.seo;
    const documentTitle = buildPageTitle(seo.title);
    this.title.setTitle(documentTitle);
    if (seo.description) this.meta.updateTag({ name: 'description', content: seo.description });
    if (seo.ogImage?.cdnUrl) {
      this.meta.updateTag({ property: 'og:image', content: seo.ogImage.cdnUrl });
      this.meta.updateTag({ property: 'og:title', content: documentTitle });
    }
  }
}
